import { useEffect, useState } from "react";

import {
  BrandPortalModal,
  Button,
  Checkbox,
  TextArea,
  TextInput,
} from "../../../../components/design-system";
import {
  STORE_TYPE_OPTIONS_LEFT_COLUMN,
  STORE_TYPE_OPTIONS_RIGHT_COLUMN,
} from "../mockData";

import { SettingsModalFooter } from "./SettingsModalFooter";

type StoreTypesModalProps = {
  open: boolean;
  selected: string[];
  onClose: () => void;
  onConfirm: (selected: string[]) => void;
  onSuggestionSubmitted?: () => void;
};

type ModalView = "select" | "suggest";

export function StoreTypesModal({
  open,
  selected,
  onClose,
  onConfirm,
  onSuggestionSubmitted,
}: StoreTypesModalProps) {
  const [view, setView] = useState<ModalView>("select");
  const [draft, setDraft] = useState<string[]>(selected);
  const [suggestedStoreType, setSuggestedStoreType] = useState("");
  const [suggestedReason, setSuggestedReason] = useState("");

  useEffect(() => {
    if (!open) return;
    setView("select");
    setDraft(selected);
    setSuggestedStoreType("");
    setSuggestedReason("");
  }, [open, selected]);

  const toggle = (storeType: string) => {
    setDraft((prev) =>
      prev.includes(storeType)
        ? prev.filter((s) => s !== storeType)
        : [...prev, storeType]
    );
  };

  const handleClose = () => {
    setView("select");
    onClose();
  };

  const handleBackToSelect = () => {
    setView("select");
    setSuggestedStoreType("");
    setSuggestedReason("");
  };

  const handleSubmitSuggestion = () => {
    const trimmedStoreType = suggestedStoreType.trim();
    if (!trimmedStoreType) return;
    onSuggestionSubmitted?.();
    handleBackToSelect();
  };

  const isSuggestView = view === "suggest";

  return (
    <BrandPortalModal
      open={open}
      onClose={isSuggestView ? handleBackToSelect : handleClose}
      title={
        isSuggestView ? "Suggest a store type" : "Select preferred store types"
      }
      width="648px"
      footerClassName="!pt-4"
      footer={
        isSuggestView ? (
          <SettingsModalFooter
            actions={
              <>
                <Button variant="secondary" onClick={handleBackToSelect}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  disabled={!suggestedStoreType.trim()}
                  onClick={handleSubmitSuggestion}
                >
                  Submit
                </Button>
              </>
            }
          />
        ) : (
          <SettingsModalFooter
            leading={`${draft.length} store type${draft.length === 1 ? "" : "s"} selected`}
            leadingClassName="text-xs leading-4 text-[#333]"
            actions={
              <>
                <button
                  type="button"
                  className="text-sm leading-5 text-[#333] underline"
                  onClick={() => setView("suggest")}
                >
                  Suggest a store type
                </button>
                <Button
                  variant="primary"
                  size="small"
                  disabled={draft.length === 0}
                  onClick={() => onConfirm(draft)}
                >
                  Confirm
                </Button>
              </>
            }
          />
        )
      }
    >
      {isSuggestView ? (
        <div className="flex flex-col gap-6">
          <p className="text-sm leading-5 text-[#333]">
            Not seeing a store type that you want to prioritize on the list? Let
            us know.
          </p>
          <TextInput
            label="Store type"
            value={suggestedStoreType}
            onChange={(event) => setSuggestedStoreType(event.target.value)}
            required
          />
          <TextArea
            label="Why this store type? (optional)"
            value={suggestedReason}
            onChange={(event) => setSuggestedReason(event.target.value)}
            rows={4}
            resize
          />
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm leading-5 text-[#333]">
            The fewer store types you select, the clearer signal you give us
            about where you&apos;d like to grow. We&apos;ll still promote you to
            other store types when there&apos;s a high likelihood of conversion.
          </p>
          <div className="flex gap-6 px-4 pt-2 pb-6">
            <div className="flex flex-1 flex-col gap-4">
              {STORE_TYPE_OPTIONS_LEFT_COLUMN.map((storeType) => (
                <Checkbox
                  key={storeType}
                  label={storeType}
                  checked={draft.includes(storeType)}
                  onChange={() => toggle(storeType)}
                />
              ))}
            </div>
            <div className="flex flex-1 flex-col gap-4">
              {STORE_TYPE_OPTIONS_RIGHT_COLUMN.map((storeType) => (
                <Checkbox
                  key={storeType}
                  label={storeType}
                  checked={draft.includes(storeType)}
                  onChange={() => toggle(storeType)}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </BrandPortalModal>
  );
}
